import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../config/supabase'

/**
 * Hook para obtener análisis de rendimiento de una gimnasta
 * @param {string} gymnastId - ID de la gimnasta
 * @returns {object} { analysis, loading, error, refetch }
 */
export const useGymnastAnalysis = (gymnastId) => {
  const [analysis, setAnalysis] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const fetchAnalysis = useCallback(async () => {
    if (!gymnastId) {
      setAnalysis(null)
      setError(null)
      return
    }

    setLoading(true)
    setError(null)

    const humanize = (raw) => {
      const msg = (raw || '').toString().toLowerCase()
      if (msg.includes('400') || msg.includes('bad request')) return 'Esta atleta aún no tiene suficiente información registrada para un análisis de IA (se necesitan al menos 2 copas con puntajes).'
      if (msg.includes('non-2xx') || msg.includes('500') || msg.includes('502') || msg.includes('503')) return 'El servicio de IA no está disponible por ahora. Intenta nuevamente en unos minutos.'
      if (msg.includes('network') || msg.includes('fetch')) return 'Sin conexión con el servicio de IA. Revisa tu internet e inténtalo de nuevo.'
      if (msg.includes('timeout')) return 'El análisis tardó demasiado. Vuelve a intentarlo.'
      if (msg.includes('unauthorized') || msg.includes('401') || msg.includes('403')) return 'No tienes permiso para usar el análisis IA. Contacta al administrador.'
      if (msg.includes('rate limit') || msg.includes('429')) return 'Has usado muchos análisis seguidos. Espera un momento antes de volver a intentar.'
      if (msg.includes('datos_insuficientes') || msg.includes('insuficientes')) return 'Se necesitan al menos 2 copas con puntajes registrados para generar el análisis IA.'
      return 'No se pudo generar el análisis en este momento. Intenta nuevamente más tarde.'
    }

    try {
      const { data, error: fnError } = await supabase.functions.invoke('analyze-gymnast', {
        body: { gymnast_id: gymnastId }
      })

      if (fnError) {
        throw new Error(fnError.message || 'Error en la función')
      }

      if (data?.error) {
        setAnalysis(null)
        setError(humanize(data.error))
        return
      }

      setAnalysis(data?.analysis || null)
    } catch (err) {
      console.error('useGymnastAnalysis error:', err)
      setError(humanize(err.message))
      setAnalysis(null)
    } finally {
      setLoading(false)
    }
  }, [gymnastId])

  useEffect(() => {
    fetchAnalysis()
  }, [fetchAnalysis])

  return {
    analysis,
    loading,
    error,
    refetch: fetchAnalysis
  }
}

export default useGymnastAnalysis